import type {
  FeeRulePreviewRequest,
  FeeRulePreviewResponse,
  FeeScheduleCreateRequest,
  FeeScheduleResponse,
  FeeScheduleUpdateRequest,
  PlatformFeeRuleInput,
  PlatformFeeRuleRecord,
  PlatformFeeRuleRequest,
} from '../types';
import type { RequestFn } from '../client';

/**
 * Merchant-scoped fee schedules. Each schedule optionally targets a
 * specific shop (via `shop_id`). Merchants can CRUD their own fee
 * overrides; platform-wide fee programs are administered by Delopay and
 * not exposed here.
 *
 * `fees.rules` manages the merchant-owned Euclid fee-rule program, which takes
 * precedence over the flat schedules above. A program is scoped either to one
 * shop (`profile_id`) or merchant-wide; a shop-scoped program wins for that
 * shop, otherwise the merchant-wide one applies. Build the program with the
 * `feeProgram()` helper.
 */
export class Fees {
  /** Merchant-owned Euclid fee-rule program (`/merchant-fees/rules`). */
  readonly rules: FeeRulesManager;

  constructor(private readonly request: RequestFn) {
    this.rules = new FeeRulesManager(request);
  }

  /**
   * Create a merchant-scoped fee schedule (optionally per-shop).
   *
   * @param params - Fee schedule parameters.
   * @param merchantId - The merchant account ID.
   */
  async create(params: FeeScheduleCreateRequest, merchantId: string): Promise<FeeScheduleResponse> {
    return this.request('POST', '/merchant-fees', {
      body: params,
      query: { merchant_id: merchantId },
    });
  }

  /**
   * List the merchant's own fee schedules.
   *
   * @param merchantId - The merchant account ID.
   */
  async list(merchantId: string): Promise<FeeScheduleResponse[]> {
    return this.request('GET', '/merchant-fees/list', {
      query: { merchant_id: merchantId },
    });
  }

  /**
   * Update a merchant-scoped fee schedule.
   *
   * @param feeId - The fee schedule ID.
   * @param params - Fields to update.
   */
  async update(feeId: string, params: FeeScheduleUpdateRequest): Promise<FeeScheduleResponse> {
    return this.request('PUT', `/merchant-fees/${encodeURIComponent(feeId)}`, { body: params });
  }

  /**
   * Delete a merchant-scoped fee schedule.
   *
   * @param feeId - The fee schedule ID.
   */
  async delete(feeId: string): Promise<FeeScheduleResponse> {
    return this.request('DELETE', `/merchant-fees/${encodeURIComponent(feeId)}`);
  }
}

/**
 * Manages a merchant's Euclid fee-rule programs (merchant-wide or per-shop, one
 * active program per scope). Build the `algorithm` with `feeProgram()`. The SDK
 * injects `fee_owner: 'merchant'`; set `profile_id` on the input to scope a
 * program to a shop.
 */
export class FeeRulesManager {
  constructor(private readonly request: RequestFn) {}

  /**
   * Create or replace the merchant's fee-rule program (a new active version;
   * the previous version is deactivated server-side).
   *
   * @param params - The program plus optional name / shop scope / validity window.
   * @param merchantId - The merchant account ID.
   */
  async upsert(params: PlatformFeeRuleInput, merchantId: string): Promise<PlatformFeeRuleRecord> {
    const body: PlatformFeeRuleRequest = { ...params, fee_owner: 'merchant' };
    return this.request('PUT', '/merchant-fees/rules', {
      body,
      query: { merchant_id: merchantId },
    });
  }

  /**
   * Retrieve the active fee-rule program for a scope, or `null` if none.
   *
   * @param merchantId - The merchant account ID.
   * @param profileId - Optional shop (`profile_id`) scope. Omit for the
   *   merchant-wide program; pass a shop id to get that shop's program.
   */
  async retrieve(merchantId: string, profileId?: string): Promise<PlatformFeeRuleRecord | null> {
    return this.request('GET', '/merchant-fees/rules', {
      query: { merchant_id: merchantId, profile_id: profileId },
    });
  }

  /**
   * Deactivate a fee-rule program (falls back to the flat fee schedules /
   * volume tier). Idempotent.
   *
   * @param merchantId - The merchant account ID.
   * @param profileId - Optional shop (`profile_id`) scope. Omit to target the
   *   merchant-wide program; pass a shop id to delete only that shop's program
   *   (other shops' programs are left intact).
   */
  async delete(merchantId: string, profileId?: string): Promise<void> {
    await this.request('DELETE', '/merchant-fees/rules', {
      query: { merchant_id: merchantId, profile_id: profileId },
    });
  }

  /**
   * Dry-run a candidate fee-rule program against a sample transaction.
   * Returns the matched rule name, whether it fell through, and the computed fee.
   * Does not persist anything.
   *
   * @param input - Candidate program + sample transaction fields.
   * @param merchantId - The merchant account ID.
   */
  async preview(input: FeeRulePreviewRequest, merchantId: string): Promise<FeeRulePreviewResponse> {
    return this.request('POST', '/merchant-fees/rules/preview', {
      body: input,
      query: { merchant_id: merchantId },
    });
  }
}
